import { z } from 'zod'

/** 聊天消息的最大长度（UTF-16 码元数）。 */
export const MAX_CHAT_LENGTH = 80

/** 显示名（房主分配的）最大长度。 */
export const MAX_NAME_LENGTH = 16

/** 身份令牌的最小/最大长度。 */
export const MIN_TOKEN_LENGTH = 8
export const MAX_TOKEN_LENGTH = 64

/**
 * 棋盘坐标。上下界（`r ≤ 9`、`c ≤ 8`）对应 9 列 10 行的中国象棋棋盘，
 * 属于有意的越界防护，不要放宽成任意数字。
 */
export const PosSchema = z.object({
  r: z.number().int().min(0).max(9),
  c: z.number().int().min(0).max(8),
})

export const MoveMessage = z.object({
  type: z.literal('move'),
  from: PosSchema,
  to: PosSchema,
  /**
   * 执子方座位。**只有房主中继给观众时才填**（观众无从推导这一步是谁走的）；
   * 玩家点对点直发时不需要，房主也会忽略对方填的值、以自己判定为准。
   */
  by: z.string().min(1).max(8).optional(),
})

export const ChatMessage = z.object({
  type: z.literal('chat'),
  text: z.string().min(1).max(MAX_CHAT_LENGTH),
  /**
   * 发送方的显示名。发送时**不必填**：房主在转发时会用自己的「peerId → 名字」表**改写**它。
   * 非房主客户端收到的消息只可能来自房主，所以这个字段是房主权威署名，不可伪造。
   */
  name: z.string().min(1).max(MAX_NAME_LENGTH).optional(),
})

/** 接入方在连接建立后立即上报的自我介绍，房主据此归还座位/观众编号。 */
export const HelloMessage = z.object({
  type: z.literal('hello'),
  /** 客户端持久化的「房间内身份令牌」（sessionStorage，按房间号隔离） */
  token: z.string().min(MIN_TOKEN_LENGTH).max(MAX_TOKEN_LENGTH),
})

/**
 * 房主的身份指派。收到它之前，接入方**不得**认为自己是玩家。
 * `seat` 只在 `role === 'player'` 时出现（观众没有座位）。
 */
export const WelcomeMessage = z.object({
  type: z.literal('welcome'),
  role: z.enum(['player', 'spectator']),
  seat: z.string().min(1).max(8).optional(),
  /** 房主分配的显示名：观众是「嘉豪N」，玩家是「红方」/「黑方」 */
  name: z.string().min(1).max(MAX_NAME_LENGTH),
})

/** 接入方（玩家或观众）请求补齐当前局面；房主必须回 SyncMessage。 */
export const SyncRequestMessage = z.object({ type: z.literal('sync-request') })

/**
 * 从开局起的完整走子序列，按时间顺序、带每步的执子方。
 * 接收方逐步过 `isLegal`/`apply` 重放，而不是直接信任一块不透明的状态。
 */
export const SyncMessage = z.object({
  type: z.literal('sync'),
  moves: z
    .array(
      z.object({
        by: z.string().min(1).max(8),
        from: PosSchema,
        to: PosSchema,
      }),
    )
    .max(4096),
})

export const RestartMessage = z.object({ type: z.literal('restart') })

/**
 * 房主广播的观众人数（谁都能看）。
 * `count` 是**当前在线**的观众数；掉线宽限期内的不算，房主本人是玩家也不计。
 * 只有房主掌握身份表，所以这个数字必须由它下发。
 */
export const AudienceMessage = z.object({
  type: z.literal('audience'),
  count: z.number().int().nonnegative(),
})

export const RoomFullMessage = z.object({ type: z.literal('room-full') })

export const PeerStateMessage = z.object({
  type: z.literal('peer-state'),
  hash: z.string().min(1).max(64),
  moveCount: z.number().int().nonnegative(),
})

export const GameMessage = z.discriminatedUnion('type', [
  MoveMessage,
  ChatMessage,
  RestartMessage,
  RoomFullMessage,
  PeerStateMessage,
  HelloMessage,
  WelcomeMessage,
  SyncRequestMessage,
  SyncMessage,
  AudienceMessage,
])

export type GameMessage = z.infer<typeof GameMessage>
export type MoveMessage = z.infer<typeof MoveMessage>
export type ChatMessage = z.infer<typeof ChatMessage>
export type RestartMessage = z.infer<typeof RestartMessage>
export type RoomFullMessage = z.infer<typeof RoomFullMessage>
export type PeerStateMessage = z.infer<typeof PeerStateMessage>
export type HelloMessage = z.infer<typeof HelloMessage>
export type WelcomeMessage = z.infer<typeof WelcomeMessage>
export type SyncRequestMessage = z.infer<typeof SyncRequestMessage>
export type SyncMessage = z.infer<typeof SyncMessage>
export type AudienceMessage = z.infer<typeof AudienceMessage>
export type MessageType = GameMessage['type']
